import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { RefreshCw, Zap, CheckCircle2, XCircle, Loader2, Cable, ArrowUpDown, Clock, Package, Save, Eye, EyeOff, ShieldCheck, Megaphone, HardDrive, Send, KeyRound, Users, FolderTree, ChevronRight, ArrowLeft, Edit3, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatRow = ({ label, value, mono }) => (
  <div className="flex items-center justify-between text-sm py-1">
    <span className="text-muted-foreground">{label}</span>
    <span className={`${mono ? 'font-mono text-xs' : 'font-medium'}`}>{value ?? <span className="text-muted-foreground italic">—</span>}</span>
  </div>
);

const AdminIntegrationsPage = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);   // 'backfill' | 'pull' | null
  const [configItems, setConfigItems] = useState([]);
  const [editValues, setEditValues] = useState({});
  const [showSecret, setShowSecret] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [testingCliq, setTestingCliq] = useState(false);
  const [testingDrive, setTestingDrive] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [authCodeRegion, setAuthCodeRegion] = useState('in');
  const [exchanging, setExchanging] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionReport, setProvisionReport] = useState(null);
  const [normalizing, setNormalizing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [maintenanceReport, setMaintenanceReport] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseStack, setBrowseStack] = useState([]); // [{id, name}]
  const [browseNodes, setBrowseNodes] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/witties/cortex/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/integrations/config`, { withCredentials: true }),
      ]);
      setStatus(statusRes.data);
      setConfigItems(configRes.data.config || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load status');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const saveKey = async (key) => {
    setSavingKey(key);
    try {
      await axios.put(`${API_URL}/api/admin/integrations/config/${key}`, { value: editValues[key] ?? '' }, { withCredentials: true });
      toast.success(`${key} saved`);
      setEditValues(prev => { const n = { ...prev }; delete n[key]; return n; });
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSavingKey(null); }
  };

  const runAction = async (kind) => {
    setRunning(kind);
    try {
      const url = kind === 'backfill' ? '/api/witties/cortex/backfill' : '/api/witties/cortex/pull';
      const res = await axios.post(`${API_URL}${url}`, {}, { withCredentials: true });
      const { fetched, inserted, skipped_duplicates, invalid } = res.data || {};
      toast.success(`${kind === 'backfill' ? 'Backfill' : 'Delta pull'} complete · fetched ${fetched ?? '?'}, new ${inserted ?? 0}, dup ${skipped_duplicates ?? 0}, invalid ${invalid ?? 0}`);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || `${kind} failed`);
    } finally { setRunning(null); }
  };

  const sendCliqTest = async () => {
    setTestingCliq(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/integrations/cliq/test`, {}, { withCredentials: true });
      toast.success(`Cliq responded HTTP ${res.data?.status_code ?? 'OK'} — check your Cliq channel now.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cliq test failed');
    } finally { setTestingCliq(false); }
  };

  const testDriveConnection = async () => {
    setTestingDrive(true);
    try {
      const res = await axios.get(`${API_URL}/api/zoho/drive/test`, { withCredentials: true });
      const d = res.data || {};
      toast.success(`Zoho Drive OK · region ${d.domain} · signed in as ${d.display_name || d.email || d.user_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zoho Drive test failed');
    } finally { setTestingDrive(false); }
  };

  const exchangeAuthCode = async () => {
    if (!authCode.trim()) { toast.error('Paste the authorization code first'); return; }
    setExchanging(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/integrations/zoho/exchange-code`,
        { auth_code: authCode.trim(), region: authCodeRegion },
        { withCredentials: true });
      const d = res.data || {};
      toast.success(`Refresh token saved (${d.refresh_token_preview}) · region ${d.region}. Now click "Test connection".`);
      setAuthCode('');
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Exchange failed');
    } finally { setExchanging(false); }
  };

  const provisionAll = async () => {
    setProvisioning(true);
    setProvisionReport(null);
    try {
      const res = await axios.post(`${API_URL}/api/zoho/drive/provision-all`, {}, { withCredentials: true });
      const t = res.data?.totals || {};
      setProvisionReport(res.data);
      toast.success(`Provisioning done · matched ${t.matched ?? 0} · created ${t.created ?? 0} · skipped ${t.skipped ?? 0} · failed ${t.failed ?? 0}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Provisioning failed');
    } finally { setProvisioning(false); }
  };

  const loadTeamFolders = async () => {
    setBrowseLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/zoho/drive/teamfolders`, { withCredentials: true });
      const tfs = (res.data?.teamfolders || []).map(t => ({ id: t.id, name: t.name || '(unnamed)', type: 'teamfolder' }));
      setBrowseStack([{ id: null, name: 'Team Folders (root)' }]);
      setBrowseNodes(tfs);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load team folders');
    } finally { setBrowseLoading(false); }
  };

  const openFolder = async (node) => {
    setBrowseLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/zoho/drive/browse/${node.id}`, { withCredentials: true });
      setBrowseStack(prev => [...prev, { id: node.id, name: node.name }]);
      setBrowseNodes(res.data?.folders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open folder');
    } finally { setBrowseLoading(false); }
  };

  const goBack = async () => {
    if (browseStack.length <= 1) { setBrowseNodes([]); setBrowseStack([]); return; }
    const newStack = browseStack.slice(0, -1);
    const parent = newStack[newStack.length - 1];
    setBrowseLoading(true);
    try {
      if (!parent.id) {
        // Root — reload team folders
        const res = await axios.get(`${API_URL}/api/zoho/drive/teamfolders`, { withCredentials: true });
        setBrowseNodes((res.data?.teamfolders || []).map(t => ({ id: t.id, name: t.name || '(unnamed)', type: 'teamfolder' })));
      } else {
        const res = await axios.get(`${API_URL}/api/zoho/drive/browse/${parent.id}`, { withCredentials: true });
        setBrowseNodes(res.data?.folders || []);
      }
      setBrowseStack(newStack);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to go back');
    } finally { setBrowseLoading(false); }
  };

  const applyAsTeamFolder = async (node) => {
    setEditValues(prev => ({ ...prev, ZOHO_DRIVE_TEAM_FOLDER_ID: node.id }));
    try {
      await axios.put(`${API_URL}/api/admin/integrations/config/ZOHO_DRIVE_TEAM_FOLDER_ID`, { value: node.id }, { withCredentials: true });
      toast.success(`Saved: HR Team Folder ID → ${node.name} (${node.id.slice(0, 12)}…)`);
      setEditValues(prev => { const n = { ...prev }; delete n.ZOHO_DRIVE_TEAM_FOLDER_ID; return n; });
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const normalizeFolderNames = async () => {
    setNormalizing(true);
    setMaintenanceReport(null);
    try {
      const res = await axios.post(`${API_URL}/api/zoho/drive/normalize-folder-names`, {}, { withCredentials: true });
      const t = res.data?.totals || {};
      setMaintenanceReport({ kind: 'rename', data: res.data });
      toast.success(`Rename done · renamed ${t.renamed ?? 0} · unchanged ${t.unchanged ?? 0} · failed ${t.failed ?? 0}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    } finally { setNormalizing(false); }
  };

  const backfillProfileDocs = async () => {
    setBackfilling(true);
    setMaintenanceReport(null);
    try {
      const res = await axios.post(`${API_URL}/api/zoho/drive/backfill-profile-docs`, {}, { withCredentials: true, timeout: 15 * 60 * 1000 });
      const t = res.data?.totals || {};
      setMaintenanceReport({ kind: 'backfill', data: res.data });
      toast.success(`Backfill done · uploaded ${t.uploaded ?? 0} · skipped ${t.skipped ?? 0} · failed ${t.failed ?? 0} across ${t.employees ?? 0} employees`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally { setBackfilling(false); }
  };

  if (user?.role !== 'admin' && user?.role !== 'hr') {
    return <div className="p-8 text-center text-muted-foreground">Admin access required.</div>;
  }

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-integrations-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Cable className="w-7 h-7 text-primary" /> Integrations
        </h1>
        <p className="text-muted-foreground">Manage sync between HR and other Wisewit apps.</p>
      </div>

      {/* Cortex Witties Sync Card */}
      <Card data-testid="cortex-sync-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" /> Cortex Witties Sync
              </CardTitle>
              <CardDescription>Bidirectional sync between hr.wisewit.ai and cortex.wisewit.ai for the shared Witties ledger.</CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={fetchStatus} disabled={loading} data-testid="refresh-status-btn" aria-label="Refresh status">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : status ? (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge className={status.configured ? 'bg-emerald-500' : 'bg-slate-400'} data-testid="cortex-configured-badge">
                  {status.configured ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                  {status.configured ? 'Configured' : 'Not configured'}
                </Badge>
                <Badge variant={status.pull_enabled ? 'default' : 'outline'} data-testid="cortex-pull-badge">
                  <ArrowUpDown className="w-3 h-3 mr-1" />
                  Scheduled pull {status.pull_enabled ? 'ON' : 'OFF'}
                </Badge>
                <Badge variant="secondary" data-testid="cortex-ingested-badge">
                  <Package className="w-3 h-3 mr-1" />
                  {status.total_cortex_witties_ingested ?? 0} witties ingested
                </Badge>
              </div>

              {!status.configured && (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3 space-y-2" data-testid="cortex-unconfigured-notice">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    Cortex sync isn&apos;t configured on this backend yet
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    Add these to <code className="bg-amber-100 dark:bg-amber-900/40 px-1 py-0.5 rounded">backend/.env</code>, then restart the backend service:
                  </p>
                  <pre className="text-[11px] bg-amber-100 dark:bg-amber-900/40 rounded p-2 overflow-x-auto"><code>{`CORTEX_BASE_URL="https://cortex.wisewit.ai"
CORTEX_SYNC_TOKEN="<same secret as Cortex's HR_SYNC_TOKEN>"
CORTEX_SYNC_ENABLED="false"   # flip to "true" once Cortex exposes /api/witties/recent`}</code></pre>
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    Until then, the Backfill and Delta pull buttons stay disabled. Push from Cortex to HR still works — that path only needs <code className="bg-amber-100 dark:bg-amber-900/40 px-1 py-0.5 rounded">CORTEX_SYNC_TOKEN</code> to be set.
                  </p>
                </div>
              )}

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <StatRow label="Cortex base URL" value={status.cortex_base_url} mono />
                <StatRow label="Last push received" value={fmtTime(status.last_push_at)} />
                <StatRow label="Last push count" value={status.last_push_count} />
                <StatRow label="Last pull run" value={fmtTime(status.last_pull_at)} />
                <StatRow label="Last pull cursor" value={status.last_pull_since} mono />
                <StatRow label="Next pull cursor" value={status.last_pull_next_since} mono />
                <StatRow label="Last pull fetched" value={status.last_pull_count} />
              </div>

              <Separator />

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => runAction('backfill')}
                  disabled={running !== null || !status.configured}
                  className="gap-1.5"
                  data-testid="cortex-backfill-btn"
                  title={!status.configured ? 'Set CORTEX_BASE_URL + CORTEX_SYNC_TOKEN in backend/.env first' : 'Fetch all Cortex witties from the start'}
                >
                  {running === 'backfill' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                  Run full backfill
                </Button>
                <Button
                  variant="outline"
                  onClick={() => runAction('pull')}
                  disabled={running !== null || !status.configured}
                  className="gap-1.5"
                  data-testid="cortex-pull-btn"
                  title={!status.configured ? 'Set CORTEX_BASE_URL + CORTEX_SYNC_TOKEN in backend/.env first' : 'Fetch witties since the last cursor'}
                >
                  {running === 'pull' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                  Delta pull (from last cursor)
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground pt-1">
                Backfill fetches every witty from Cortex without a cursor. Delta pull uses the last cursor. Both are idempotent — re-running is safe.
              </p>
            </>
          ) : (
            <p className="text-center text-muted-foreground py-6">Could not load status.</p>
          )}
        </CardContent>
      </Card>
      {/* Per-Integration Configuration Forms */}
      <Card data-testid="integrations-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Save className="w-5 h-5 text-primary" /> Configuration</CardTitle>
          <CardDescription>Set URLs and API keys here without editing <code className="text-xs bg-muted px-1 py-0.5 rounded">backend/.env</code>. Values saved here take precedence over env vars.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {['cortex', 'hcaptcha', 'zoho_cliq', 'zoho_drive'].map(group => {
            const items = configItems.filter(c => c.integration === group);
            if (!items.length) return null;
            const groupLabel = {
              cortex: { title: 'Cortex Sync', icon: Zap },
              hcaptcha: { title: 'hCaptcha (Careers Spam Guard)', icon: ShieldCheck },
              zoho_cliq: { title: 'Zoho Cliq (Leave Announcements)', icon: Megaphone },
              zoho_drive: { title: 'Zoho Drive (Document Storage)', icon: HardDrive },
            }[group];
            const Icon = groupLabel.icon;
            return (
              <div key={group} className="space-y-3" data-testid={`config-group-${group}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-semibold text-sm">{groupLabel.title}</h3>
                  </div>
                  {group === 'zoho_cliq' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={sendCliqTest}
                      disabled={testingCliq}
                      className="gap-1"
                      data-testid="cliq-test-btn"
                      title="Sends a canned test message to the configured Cliq webhook"
                    >
                      {testingCliq ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Send test message
                    </Button>
                  )}
                  {group === 'zoho_drive' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={testDriveConnection}
                      disabled={testingDrive}
                      className="gap-1"
                      data-testid="drive-test-btn"
                      title="Verifies OAuth credentials by hitting Zoho WorkDrive /users/me"
                    >
                      {testingDrive ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Test connection
                    </Button>
                  )}
                </div>
                {items.map(item => {
                  const isDirty = editValues[item.key] !== undefined;
                  const displayValue = isDirty ? editValues[item.key] : (item.secret ? item.value_masked : (item.value_masked || ''));
                  const inputType = (item.secret && !showSecret[item.key]) ? 'password' : 'text';
                  return (
                    <div key={item.key} className="grid grid-cols-1 sm:grid-cols-[220px_1fr_auto] gap-2 items-start" data-testid={`config-row-${item.key}`}>
                      <div className="pt-2">
                        <Label className="text-xs font-medium">{item.label}</Label>
                        {item.help && <p className="text-[10px] text-muted-foreground">{item.help}</p>}
                      </div>
                      <div className="relative">
                        <Input
                          type={inputType}
                          value={displayValue}
                          onChange={e => setEditValues(prev => ({ ...prev, [item.key]: e.target.value }))}
                          placeholder={item.configured ? '' : `unset (falls back to .env: ${item.key})`}
                          className="pr-9 text-sm font-mono"
                          data-testid={`config-input-${item.key}`}
                        />
                        {item.secret && (
                          <button
                            type="button"
                            onClick={() => setShowSecret(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            data-testid={`config-toggle-${item.key}`}
                          >
                            {showSecret[item.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant={isDirty ? 'default' : 'outline'}
                        disabled={!isDirty || savingKey === item.key}
                        onClick={() => saveKey(item.key)}
                        className="gap-1"
                        data-testid={`config-save-${item.key}`}
                      >
                        {savingKey === item.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                      </Button>
                    </div>
                  );
                })}
                {group === 'zoho_drive' && (
                  <div className="mt-2 rounded-md border bg-muted/30 p-3 space-y-2" data-testid="zoho-browse-panel">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <FolderTree className="w-4 h-4 text-muted-foreground" />
                        <h4 className="text-xs font-semibold">Browse WorkDrive → pick the correct HR Team Folder ID</h4>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={loadTeamFolders}
                        disabled={browseLoading}
                        className="gap-1"
                        data-testid="zoho-browse-load-btn"
                      >
                        {browseLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderTree className="w-3.5 h-3.5" />}
                        {browseStack.length ? 'Refresh' : 'Load team folders'}
                      </Button>
                    </div>
                    {browseStack.length > 0 && (
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground flex-wrap">
                          {browseStack.length > 1 && (
                            <Button size="sm" variant="ghost" onClick={goBack} className="h-6 px-1.5 gap-1" data-testid="zoho-browse-back">
                              <ArrowLeft className="w-3 h-3" /> Back
                            </Button>
                          )}
                          {browseStack.map((s, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && <ChevronRight className="w-3 h-3" />}
                              <span className="truncate max-w-[200px]">{s.name}</span>
                            </React.Fragment>
                          ))}
                        </div>
                        <div className="max-h-56 overflow-y-auto border rounded bg-background">
                          {browseNodes.length === 0 ? (
                            <p className="text-[11px] text-muted-foreground p-2">No subfolders here.</p>
                          ) : browseNodes.map(node => (
                            <div key={node.id} className="flex items-center justify-between gap-2 px-2 py-1.5 border-b last:border-b-0 hover:bg-muted/50" data-testid={`zoho-browse-node-${node.id}`}>
                              <button
                                type="button"
                                onClick={() => openFolder(node)}
                                className="flex-1 text-left text-xs font-medium truncate hover:underline"
                              >
                                📁 {node.name}
                              </button>
                              <code className="text-[10px] text-muted-foreground font-mono truncate max-w-[140px]">{node.id}</code>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => applyAsTeamFolder(node)}
                                className="h-6 text-[11px] px-2 gap-1"
                                data-testid={`zoho-use-folder-${node.id}`}
                              >
                                Use this
                              </Button>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          Tip: navigate into <code>00_Management_Governance</code>, then <code>05_Employees</code>, and click <b>Use this</b>. That saves its ID as the HR Team Folder ID.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {group === 'zoho_drive' && (
                  <div className="mt-2 rounded-md border bg-muted/30 p-3 space-y-2" data-testid="zoho-code-exchange">
                    <div className="flex items-center gap-2">
                      <KeyRound className="w-4 h-4 text-muted-foreground" />
                      <h4 className="text-xs font-semibold">Exchange authorization code for refresh token</h4>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Paste the one-time authorization code from Zoho API Console → Self Client → <em>Generate Code</em>.
                      Save your Client ID + Client Secret above first. The code expires in 3–10 minutes, so run this right after generating it.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-2">
                      <Input
                        type="text"
                        placeholder="1000.xxxxxxx auth code..."
                        value={authCode}
                        onChange={e => setAuthCode(e.target.value)}
                        className="text-sm font-mono"
                        data-testid="zoho-auth-code-input"
                      />
                      <select
                        value={authCodeRegion}
                        onChange={e => setAuthCodeRegion(e.target.value)}
                        className="border rounded-md px-2 text-sm bg-background"
                        data-testid="zoho-auth-region"
                      >
                        <option value="com">.com</option>
                        <option value="in">.in</option>
                        <option value="eu">.eu</option>
                        <option value="com.au">.com.au</option>
                        <option value="jp">.jp</option>
                      </select>
                      <Button
                        size="sm"
                        onClick={exchangeAuthCode}
                        disabled={exchanging || !authCode.trim()}
                        className="gap-1"
                        data-testid="zoho-exchange-btn"
                      >
                        {exchanging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                        Exchange
                      </Button>
                    </div>
                  </div>
                )}
                {group === 'zoho_drive' && (
                  <div className="mt-2 rounded-md border bg-muted/30 p-3 space-y-2" data-testid="zoho-provision-panel">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <h4 className="text-xs font-semibold">Match &amp; provision all employees</h4>
                      </div>
                      <Button
                        size="sm"
                        onClick={provisionAll}
                        disabled={provisioning}
                        className="gap-1"
                        data-testid="zoho-provision-btn"
                      >
                        {provisioning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                        Provision all
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Reuses folders inside the HR Team Folder whose name matches an employee&apos;s name (case-insensitive). Creates new folders only when no match is found. Idempotent — safe to re-run.
                    </p>
                    <div className="flex gap-2 flex-wrap pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={normalizeFolderNames}
                        disabled={normalizing || backfilling}
                        className="gap-1"
                        data-testid="zoho-normalize-btn"
                        title="Renames every provisioned folder to {Name} (email). Fixes UUID suffixes."
                      >
                        {normalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
                        Fix folder names
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={backfillProfileDocs}
                        disabled={normalizing || backfilling}
                        className="gap-1"
                        data-testid="zoho-backfill-btn"
                        title="Copies every existing profile document into the employee's Drive folder. Skips duplicates."
                      >
                        {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
                        Backfill profile docs → Drive
                      </Button>
                    </div>
                    {provisionReport && (
                      <div className="text-[11px] mt-2 space-y-1" data-testid="zoho-provision-report">
                        <div className="flex gap-3 flex-wrap">
                          <Badge variant="secondary">Matched {provisionReport.totals?.matched ?? 0}</Badge>
                          <Badge variant="secondary">Created {provisionReport.totals?.created ?? 0}</Badge>
                          <Badge variant="outline">Skipped {provisionReport.totals?.skipped ?? 0}</Badge>
                          {(provisionReport.totals?.failed ?? 0) > 0 && (
                            <Badge className="bg-red-500">Failed {provisionReport.totals.failed}</Badge>
                          )}
                        </div>
                        {(provisionReport.failed || []).slice(0, 5).map((f, i) => (
                          <div key={i} className="text-red-600 dark:text-red-400 truncate">
                            ❌ {f.name} — {String(f.error).slice(0, 120)}
                          </div>
                        ))}
                      </div>
                    )}
                    {maintenanceReport && (
                      <div className="text-[11px] mt-2 space-y-1" data-testid="zoho-maintenance-report">
                        {maintenanceReport.kind === 'rename' && (
                          <div className="flex gap-3 flex-wrap">
                            <Badge variant="secondary">Renamed {maintenanceReport.data.totals?.renamed ?? 0}</Badge>
                            <Badge variant="outline">Unchanged {maintenanceReport.data.totals?.unchanged ?? 0}</Badge>
                            {(maintenanceReport.data.totals?.failed ?? 0) > 0 && (
                              <Badge className="bg-red-500">Failed {maintenanceReport.data.totals.failed}</Badge>
                            )}
                          </div>
                        )}
                        {maintenanceReport.kind === 'backfill' && (
                          <div className="flex gap-3 flex-wrap">
                            <Badge variant="secondary">Uploaded {maintenanceReport.data.totals?.uploaded ?? 0}</Badge>
                            <Badge variant="outline">Skipped {maintenanceReport.data.totals?.skipped ?? 0}</Badge>
                            {(maintenanceReport.data.totals?.failed ?? 0) > 0 && (
                              <Badge className="bg-red-500">Failed {maintenanceReport.data.totals.failed}</Badge>
                            )}
                            <Badge variant="outline">{maintenanceReport.data.totals?.employees ?? 0} employees</Badge>
                          </div>
                        )}
                        {(maintenanceReport.data.failed || []).slice(0, 5).map((f, i) => (
                          <div key={i} className="text-red-600 dark:text-red-400 truncate">
                            ❌ {f.name || f.filename} — {String(f.error).slice(0, 120)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-[11px] text-muted-foreground pt-2 border-t">
            <span className="font-medium">Note:</span> secrets are stored in MongoDB (<code className="bg-muted px-1 py-0.5 rounded">integrations_config</code> collection). At rest they are stored as plaintext — future iteration will add app-level encryption. Rotate secrets by re-saving here; the change takes effect immediately (no restart).
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminIntegrationsPage;
